import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Save, Printer, CheckCircle2, AlertCircle, Shield, FileCheck, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { exportFormPdfFromDom } from '../../utils/domFormPdf';
import { fillEmpty, mergeSaved } from '../../utils/formMerge';
import SignatureCanvas from 'react-signature-canvas';

const ConsentVerificationForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId');
  const clientSignatureRef = useRef(null);
  const guardianSignatureRef = useRef(null);
  const staffSignatureRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Patient Info
    patientName: '',
    
    // Consent checkboxes
    consentToAdmission: false,
    understandCarePlan: false,
    understandSupervision: false,
    rightToRefuseCare: false,
    
    // Documents Received
    documentsReceived: {
      welcomeLetter: false,
      admissionCriteria: false,
      rightsResponsibilities: false,
      agencyResponsibilities: false,
      complaintsGrievances: false,
      rightsOfElderly: false,
      hipaaNotice: false,
      medicaidFraud: false,
      abuseNeglectPolicy: false,
      advanceDirectives: false,
      infectionControl: false,
      familyDisasterPlan: false,
      emergencyNumbers: false,
      homeSafety: false,
      tornadoPlan: false,
      evacuationAssistance: false,
      emergencyInfo: false,
      billingAgreement: false,
      servicePlanSupervision: false,
      emergencyPreparedness: false,
      homeAssessment: false,
      medicationProfile: false,
      vitalSigns: false,
      carePlan: false,
      covidScreening: false,
    },
    
    // HIPAA Consent
    hipaaRestrictInfo: false,
    hipaaAllowedPersons: '',
    hipaaAllowedPhone: '',
    releaseInfoAuthorized: false,
    
    // Advance Directives
    hasLivingWill: '',
    livingWillLocation: '',
    hasPOA: '',
    poaLocation: '',
    hasDNR: '',
    dnrLocation: '',
    hasMentalHealthDeclaration: '',
    mentalHealthLocation: '',
    
    // Guardian Info
    hasLegalGuardian: false,
    guardianName: '',
    guardianRelationship: '',
    guardianPhone: '',
    
    // Signatures
    signatureDate: new Date().toISOString().split('T')[0],
    printedName: '',
    staffName: '',
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      api.get(`/admission-forms/${patientId}/consent_verification`).then(r => { const d = r.data?.form?.data; if (d) setFormData(prev => mergeSaved(prev, d)); }).catch(() => {});
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        const patient = response.data.patient || response.data;
        setFormData(prev => fillEmpty(prev, {
          
          patientName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          printedName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        }));
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('doc_')) {
      const docKey = name.replace('doc_', '');
      setFormData(prev => ({
        ...prev,
        documentsReceived: { ...prev.documentsReceived, [docKey]: checked }
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const signatures = {
        clientSignature: clientSignatureRef.current && !clientSignatureRef.current.isEmpty() 
          ? clientSignatureRef.current.toDataURL() : '',
        guardianSignature: guardianSignatureRef.current && !guardianSignatureRef.current.isEmpty()
          ? guardianSignatureRef.current.toDataURL() : '',
        staffSignature: staffSignatureRef.current && !staffSignatureRef.current.isEmpty()
          ? staffSignatureRef.current.toDataURL() : '',
      };

      const payload = {
        ...formData,
        ...signatures,
        patientId,
        formType: 'consent_verification',
      };

      await api.post('/admission-forms/save', payload);
      toast.success('Consent & Verification form saved successfully');
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => exportFormPdfFromDom(document.getElementById('main-content'), 'Consent & Verification of Receipt', patientId);

  const selectAllDocuments = () => {
    const allChecked = Object.fromEntries(
      Object.keys(formData.documentsReceived).map(key => [key, true])
    );
    setFormData(prev => ({ ...prev, documentsReceived: allChecked }));
  };

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <p className="text-yellow-800">Please select a patient to complete this form.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="consent-verification-form">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-t-xl p-6 text-white print:bg-indigo-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <FileCheck className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Consent & Verification of Receipt</h1>
                <p className="text-indigo-100">IVORY HOME HEALTH CARE, INC.</p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                data-testid="print-form-btn"
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-b-xl shadow-lg p-8 space-y-8">
          {/* Consent Section */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-indigo-500" />
              Consent to Care
            </h2>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="consentToAdmission"
                  checked={formData.consentToAdmission}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I consent to admission and care by IVORY HOME HEALTH CARE, INC.
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="understandCarePlan"
                  checked={formData.understandCarePlan}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I understand my care plan and any changes that may be made to it
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="understandSupervision"
                  checked={formData.understandSupervision}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I understand that services will be supervised by the agency supervisor
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="rightToRefuseCare"
                  checked={formData.rightToRefuseCare}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I understand my right to refuse care at any time
                </span>
              </label>
            </div>
          </section>

          {/* Documents Received */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Documents Read and Understood</h2>
              <button
                type="button"
                onClick={selectAllDocuments}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Select All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { key: 'welcomeLetter', label: 'Welcome Letter / Hours of Operation' },
                { key: 'admissionCriteria', label: 'Admission Criteria' },
                { key: 'rightsResponsibilities', label: 'Client Rights & Responsibilities' },
                { key: 'agencyResponsibilities', label: 'Agency Responsibilities' },
                { key: 'complaintsGrievances', label: 'Complaints & Grievances' },
                { key: 'rightsOfElderly', label: 'Rights of the Elderly' },
                { key: 'hipaaNotice', label: 'HIPAA Notice of Privacy Practices' },
                { key: 'medicaidFraud', label: 'Medicaid Fraud Reporting' },
                { key: 'abuseNeglectPolicy', label: 'Abuse, Neglect & Exploitation Policy' },
                { key: 'advanceDirectives', label: 'Advance Directives' },
                { key: 'infectionControl', label: 'Infection Control' },
                { key: 'familyDisasterPlan', label: 'Family Disaster Plan' },
                { key: 'emergencyNumbers', label: 'Emergency Telephone Numbers' },
                { key: 'homeSafety', label: 'Home Safety Guidelines' },
                { key: 'tornadoPlan', label: 'Tornado Preparedness Plan' },
                { key: 'evacuationAssistance', label: 'Evacuation Assistance' },
                { key: 'emergencyInfo', label: 'Emergency Information Form' },
                { key: 'billingAgreement', label: 'Billing Agreement' },
                { key: 'servicePlanSupervision', label: 'Service Plan Supervision' },
                { key: 'emergencyPreparedness', label: 'Emergency Preparedness & Risk' },
                { key: 'homeAssessment', label: 'Personal Care Home Assessment' },
                { key: 'medicationProfile', label: 'Medication Profile' },
                { key: 'vitalSigns', label: 'Vital Signs Record' },
                { key: 'carePlan', label: 'Care Plan & Progress Notes' },
                { key: 'covidScreening', label: 'COVID-19 Screening' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    name={`doc_${key}`}
                    checked={formData.documentsReceived[key]}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* HIPAA Consent */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" />
              HIPAA Consent
            </h2>
            <div className="space-y-4">
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="hipaaRestrictInfo"
                  checked={formData.hipaaRestrictInfo}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I request to restrict my health information to specific persons only
                </span>
              </label>
              
              {formData.hipaaRestrictInfo && (
                <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Person(s)</label>
                    <input
                      type="text"
                      name="hipaaAllowedPersons"
                      value={formData.hipaaAllowedPersons}
                      onChange={handleChange}
                      placeholder="Names of people who can receive info"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="hipaaAllowedPhone"
                      value={formData.hipaaAllowedPhone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  name="releaseInfoAuthorized"
                  checked={formData.releaseInfoAuthorized}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I authorize the release of my medical information to insurance/payer for payment purposes
                </span>
              </label>
            </div>
          </section>

          {/* Advance Directives */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Advance Directives</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Living Will / Directive to Physician', hasField: 'hasLivingWill', locationField: 'livingWillLocation' },
                { label: 'Medical Power of Attorney', hasField: 'hasPOA', locationField: 'poaLocation' },
                { label: 'Out-of-Hospital DNR', hasField: 'hasDNR', locationField: 'dnrLocation' },
                { label: 'Mental Health Declaration', hasField: 'hasMentalHealthDeclaration', locationField: 'mentalHealthLocation' },
              ].map(({ label, hasField, locationField }) => (
                <div key={hasField} className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900 mb-2">{label}</p>
                  <div className="flex gap-4 mb-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={hasField}
                        value="yes"
                        checked={formData[hasField] === 'yes'}
                        onChange={handleChange}
                        className="w-4 h-4 text-indigo-500 focus:ring-indigo-500"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={hasField}
                        value="no"
                        checked={formData[hasField] === 'no'}
                        onChange={handleChange}
                        className="w-4 h-4 text-indigo-500 focus:ring-indigo-500"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                  {formData[hasField] === 'yes' && (
                    <input
                      type="text"
                      name={locationField}
                      value={formData[locationField]}
                      onChange={handleChange}
                      placeholder="Location of document"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Legal Guardian */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Legal Guardian (if applicable)</h2>
            <label className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                name="hasLegalGuardian"
                checked={formData.hasLegalGuardian}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Patient has a legal guardian</span>
            </label>
            
            {formData.hasLegalGuardian && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Name</label>
                  <input
                    type="text"
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input
                    type="text"
                    name="guardianRelationship"
                    value={formData.guardianRelationship}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="guardianPhone"
                    value={formData.guardianPhone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </section>

          {/* Signatures */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Signatures</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={clientSignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => clientSignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Printed Name</label>
                <input
                  type="text"
                  name="printedName"
                  value={formData.printedName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              {formData.hasLegalGuardian && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Guardian Signature</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    <SignatureCanvas
                      ref={guardianSignatureRef}
                      canvasProps={{
                        className: 'w-full h-24 bg-white',
                        style: { width: '100%', height: '96px' }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => guardianSignatureRef.current?.clear()}
                    className="mt-1 text-xs text-red-600 hover:text-red-700"
                  >
                    Clear
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agency Staff Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={staffSignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => staffSignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
                <input
                  type="text"
                  name="staffName"
                  value={formData.staffName}
                  onChange={handleChange}
                  placeholder="Staff name"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                name="signatureDate"
                value={formData.signatureDate}
                onChange={handleChange}
                className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConsentVerificationForm;
